"use client";
import close from "@/assets/images/shape/close.svg";
import add_to_cart_with_blue_background from "@/assets/images/logo/add_to_cart_with_blue_background..svg";
import filterIcon from "@/assets/images/shape/filter.svg";
import Loader from "@/components/Loader/Loader";
import ClearCart from "@/components/clearCart";
import BuyButton from "@/components/common/BuyButton";
import RemoveEventModal from "@/modals/RemoveEventModal";
import {
  addVivaItem,
  VivaCartItem,
  selectVivaCartItems,
  removeVivaItem,
  removeAndAddVivaItems,
} from "@/redux/features/vivaCartSlice";
import {
  setCount,
  setEvents,
  setLoading,
  setPaginatedEvents,
} from "@/redux/features/eventSlice";
import {
  addFilter,
  clearFilter,
  filterItems,
} from "@/redux/features/filterSlice";
import axiosInstance from "@/service/axiosInstance";
import {
  correctUrlString,
  groupkebabCase,
  mapToVivaCartItem,
} from "@/utils/utils";
import Pagination from "@mui/material/Pagination";
import Image from "next/image";
import Link from "next/link";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import FilterForm from "./filter-form";
import EventCard, { EventGroupAggregate } from "./event-card";
import CartButton from "../common/CartButton";
import featureIcon_4 from "@/assets/images/icon/Event2.svg";
import {
  emptyFilter,
  EVENT_CARD_DEFAULT_IMG,
  ORDER_TYPE,
  ToastOptions,
  USER_ROLE,
} from "@/utils/enum";
import { toast } from "react-toastify";
import { setItem, setVenueSchool } from "@/redux/features/bulkUploadSlice";

const VivaEventListing = () => {
  const searchParams = useParams();
  const params = new URLSearchParams();
  const filter = useSelector(filterItems);
  // console.log("🚀 ~ VivaEventListing ~ filter:", filter);
  const queryParams = useSearchParams();
  // const swapData = useSelector((state: any) => state.swapData.data);
  const userRoleVal = useSelector((state: any) => state.auth.userRole);
  if (
    searchParams?.slug &&
    Array.isArray(searchParams.slug) &&
    searchParams.slug.length > 1
  ) {
    searchParams.second = decodeURIComponent(searchParams.slug[1]);
  }
  const itemsPerPage = 8;

  const cart = useSelector(selectVivaCartItems);
  const dispatch = useDispatch();
  const router = useRouter();
  const event = useSelector((state: any) => state.events.events);
  const paginatedEvents = useSelector(
    (state: any) => state.events.paginatedEvents,
  );
  const count = useSelector((state: any) => state.events.count);
  const loading = useSelector((state: any) => state.events.eventLoading);
  const cartItemCount = useSelector((state: any) => state.cart.items);
  const [currentPage, setcurrentPage] = useState(filter.currentPage || 1);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 576);
  const [showFilter, setShowFilter] = useState(false);
  const [toggleRemoveItemsModal, setToggleRemoveItemsModal] = useState(false);
  const [clearCart, setClearCart] = useState(false);
  const startItem = Math.min(count, (currentPage - 1) * itemsPerPage + 1);
  const endItem = Math.min(count, currentPage * itemsPerPage);
  const [showModal, setShowModal] = useState(true);
  const [search, setSearch] = useState(filter.eventSearchBox || "");
  const [schools, setSchools] = useState<any>([]);

  const [filterTop, setFilterTop] = useState(104); // px, updated after mount

  const itemToAdd: any = useRef(null);
  const filterRef = useRef<HTMLDivElement>(null);
  const rawEventsRef = useRef<any[]>([]);
  const handleClose = () => setShowModal(false);
  const [error, setError] = useState("");

  // ---- Grouped event aggregates (grades, age, price, gender, etc.) ----
  const [aggregates, setAggregates] = useState<
    Record<string, EventGroupAggregate>
  >({});
  const [aggLoading, setAggLoading] = useState(false);
  const fetchedGroupsRef = useRef<Set<string>>(new Set());

  if (filter.age) {
    params.append("age", String(filter.age));
  }

  if (filter.grade) {
    params.append("grade", String(filter.grade));
  }

  if (filter?.location?.value) {
    params.append("location", filter?.location?.value);
  }

  if (filter?.school?.value) {
    params.append("venue", filter?.school?.value);
  }

  const normalizeGroupKey = (name: string) => (name || "").trim().toLowerCase();

  const fetchAggregates = async (events: any[]) => {
    const groupNames: string[] = Array.from(
      new Set(
        (events || [])
          .map((e: any) => e?.group_name)
          .filter(Boolean)
          .filter(
            (name: string) =>
              !fetchedGroupsRef.current.has(normalizeGroupKey(name)),
          ),
      ),
    );

    if (groupNames.length === 0) return;
    groupNames.forEach((name) =>
      fetchedGroupsRef.current.add(normalizeGroupKey(name)),
    );

    try {
      setAggLoading(true);
      const resp: any = await axiosInstance.post(
        "/events/v3/group-aggregates",
        {
          group_names: groupNames,
          isPrivate: filter?.privateEvents || false,
          registrationsOpen: filter?.registrationsOpen ?? true,
          age: filter.age && Number(filter.age),
          grade: filter.grade && Number(filter.grade),
          school: filter.school?.value,
          location: filter.location?.school_parent_ids,
        },
      );
      if (resp?.aggregates) {
        setAggregates((prev) => ({ ...prev, ...resp.aggregates }));
      }
    } catch (err) {
      // Cards degrade gracefully — chips simply stay hidden on failure
      groupNames.forEach((name) =>
        fetchedGroupsRef.current.delete(normalizeGroupKey(name)),
      );
      console.log("Error fetching group aggregates:", err);
    } finally {
      setAggLoading(false);
    }
  };
  useEffect(() => {
    fetchedGroupsRef.current.clear();
    setAggregates({});
  }, [filter.age, filter.grade, filter.school?.value, filter.privateEvents]);

  useEffect(() => {
    if (paginatedEvents?.length > 0 || count > 0) {
      fetchAggregates(paginatedEvents);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginatedEvents]);

  useEffect(() => {
    if (rawEventsRef.current.length === 0) return;

    const searchTerm = (filter.eventSearchBox || "").trim().toLowerCase();

    const filtered = searchTerm
      ? rawEventsRef.current.filter((item: any) =>
          item?.group_name?.toLowerCase().includes(searchTerm),
        )
      : rawEventsRef.current;

    dispatch(setEvents(filtered));
    dispatch(setCount(filtered.length));

    setcurrentPage(1);

    dispatch(setPaginatedEvents(filtered.slice(0, itemsPerPage)));
  }, [filter.eventSearchBox, dispatch]);

  const highlightFilter = () => {
    if (isMobile) {
      setShowFilter(true);
    } else {
      filterRef.current?.scrollIntoView({ behavior: "smooth" });

      const handleAnimationEnd = () => {
        filterRef.current?.classList.remove("highlight-border");
        filterRef.current?.removeEventListener(
          "animationend",
          handleAnimationEnd,
        );
      };

      filterRef.current?.classList.add("highlight-border");
      filterRef.current?.addEventListener("animationend", handleAnimationEnd);
    }
  };

  const getData = async (currentPage: number, filters: any) => {
    try {
      dispatch(setLoading(true));
      setShowFilter(false);

      // Some "Event" checkboxes (e.g. Star Ensemble) are actually Group Names,
      // not fees_sub_type values. Split them out so the sub_type query stays
      // valid and the group-name selections are matched client-side below.
      // const selectedEventsType: string[] = filters?.eventsType || [];
      // const selectedGroupNames: string[] = selectedEventsType.filter(
      //   (item: string) => GROUP_NAME_EVENT_OPTIONS.includes(item),
      // );
      // const selectedSubTypes: string[] = selectedEventsType.filter(
      //   (item: string) => !GROUP_NAME_EVENT_OPTIONS.includes(item),
      // );

      // Convert empty arrays to null to match backend expectations
      const preparedFilters = {
        zone: filters?.location ? filters?.location.value : null,
        school_id: filters?.school_id?.length ? filters.school_id : null,
        fee_category: filters?.category?.length ? filters.category : null,
        sub_type: filters?.eventsType?.length ? filters.eventsType : null,
        eventsMode: filters.eventsMode?.length ? filters.eventsMode : null,
        age: filters.age ? filters.age : null,
        grade: filters.grade ? filters.grade : null,
        // title: filters.title?.length ? filters.title : null,
        ...(filters.privateEvents && {
          isPrivate: filters.privateEvents,
        }),
      };
      const resp: any = await axiosInstance.post("/events/v3", preparedFilters);
      let updated: any = resp;

      updated = updated.map((item: any) => {
        const match = cart.find((val: any) => {
          return (
            val?.group_name === item?.group_name &&
            val?.sub_type === item["Sub Type"] &&
            val?.Category === item?.Category
          );
        });
        return {
          ...item,
          inCart: match?.inCart || 0,
        };
      });

      // Apply selected group-name filters (e.g. Star Ensemble). Exact,
      // case-insensitive match so "Star Ensemble" does not also pull in
      // "Junior Star Ensemble" events.
      // if (selectedGroupNames.length) {
      //   const normalizedSelected = selectedGroupNames.map(normalizeGroupKey);
      //   updated = updated.filter((item: any) =>
      //     normalizedSelected.includes(normalizeGroupKey(item?.group_name)),
      //   );
      // }

      rawEventsRef.current = updated;

      if (filters.eventSearchBox?.trim()) {
        const searchTerm = filters.eventSearchBox.trim().toLowerCase();
        updated = updated.filter((item: any) =>
          item?.group_name?.toLowerCase().includes(searchTerm),
        );
      }

      dispatch(setLoading(false));
      dispatch(setEvents(updated));
      dispatch(setCount(updated.length));
      const requestedPage = filters.currentPage || currentPage;
      const lastPage = Math.ceil(updated.length / itemsPerPage);
      const targetPage = Math.min(requestedPage, lastPage > 0 ? lastPage : 1);
      setcurrentPage(targetPage);
      const params = new URLSearchParams(queryParams?.toString());
      if (targetPage > 1) {
        params.set("pageNo", String(targetPage));
      } else {
        params.delete("pageNo");
      }
      const newQuery = params.toString();
      const currentQuery = queryParams?.toString() || "";
      if (newQuery !== currentQuery) {
        router.replace(newQuery ? `/events?${newQuery}` : "/events", {
          scroll: false,
        });
      }
      handlePageChange(targetPage, updated);
    } catch (error) {
      console.log("Error fetching events:", error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleRemoveFromCart = (data: VivaCartItem) => {
    dispatch(
      removeVivaItem({
        data,
        persist: false,
        strictRemove: false,
      }),
    );
  };

  const removePreviousEvents = () => {
    dispatch(removeAndAddVivaItems(itemToAdd.current));
    setToggleRemoveItemsModal(false);
  };

  const closeEventModal = () => {
    setToggleRemoveItemsModal(false);
  };

  const handleResetFilter = () => {
    dispatch(clearFilter());
    setSearch("");
    getData(currentPage, emptyFilter);
  };

  const handlePageChange = (pageNumber: number, data?: any[]) => {
    setcurrentPage(pageNumber);

    const element = document.getElementById("cart-section");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }

    const startIndex = (pageNumber - 1) * itemsPerPage;
    dispatch(
      setPaginatedEvents(
        data
          ? data.slice(startIndex, startIndex + itemsPerPage)
          : event.slice(startIndex, startIndex + itemsPerPage),
      ),
    );
  };

  const onPageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setcurrentPage(value);
    dispatch(addFilter({ ...filter, currentPage: value }));
    handlePageChange(value);
  };

  const handleOnclick = async (item: any) => {
    setLoading(true);
    const token = localStorage.getItem("token");

    if (!token) {
      router.push(`/login?redirect="true"`);
    }
    setLoading(false);
    router.push("/cart");
  };

  const handleAddToCart = async (rawData: any) => {
    rawData = {
      ...rawData,
      host_school_name: filter?.school?.value || "",
    };
    const data = mapToVivaCartItem(rawData);
    const redux_response = dispatch(addVivaItem({ data, persist: true }));

    if (typeof redux_response === "boolean") {
      itemToAdd.current = { data, persist: false };
      setToggleRemoveItemsModal(true);
      return false;
    }

    return true;
  };
  // const handleAddToCart = (item: CartItem) => {
  //   dispatch(addVivaItem({ data: item, persist: false }));
  // };
  const handleBulkUpload = async (item: any) => {
    const venue_name = filter?.school;
    if (venue_name) {
      dispatch(setVenueSchool(venue_name));
    }
    console.log("bulk-upload item:", item);
    setLoading(true);
    try {
      const response: any = await axiosInstance.delete(
        "bulkUploadCsv/deleteData",
      );
      dispatch(setItem(item));
      router.push("/bulk-upload");
    } catch (error) {
      toast.error(
        "Something went wrong while fetching user csv data!",
        ToastOptions,
      );
      console.log("handleBulkUpload ===>", { error });
    } finally {
      setLoading(false);
    }
  };
  const skipUrlSyncRef = useRef(false);
  useEffect(() => {
    const queryString = queryParams?.toString();
    let newFilter: any;

    if (!queryString) {
      newFilter = {
        ...emptyFilter,
        currentPage: 1,
      };
    } else {
      const urlSearch = queryParams?.get("search")?.trim() || "";

      const pageNo = Number(queryParams?.get("pageNo")) || 1;

      const events =
        queryParams
          ?.getAll("event")
          ?.map((item) => item.trim())
          .filter(Boolean) || [];

      const eventType =
        queryParams
          ?.getAll("eventType")
          ?.map((item) => item.trim())
          .filter(Boolean) || [];

      const venue = queryParams?.get("venue")?.trim() || "";

      const schoolId =
        queryParams
          ?.getAll("schoolId")
          ?.map((item) => item.trim())
          .filter(Boolean) || [];

      newFilter = {
        ...emptyFilter,
        eventSearchBox: urlSearch,
        eventsType: events,
        eventsMode: eventType,
        school: venue
          ? {
              value: venue,
              label: venue,
            }
          : null,
        school_id: schoolId,
        currentPage: pageNo,
      };
    }

    skipUrlSyncRef.current = true;

    if (search !== newFilter.eventSearchBox) {
      setSearch(newFilter.eventSearchBox || "");
    }

    dispatch(addFilter(newFilter));
    getData(1, newFilter);
  }, [queryParams]);

  useEffect(() => {
    if (!filter) return;
    if (skipUrlSyncRef.current) {
      // console.log("🚀 ~ Filter -> URL ~ skipped initial URL sync");
      skipUrlSyncRef.current = false;
      return;
    }
    const params = new URLSearchParams();

    if (filter.eventSearchBox?.trim()) {
      params.set("search", filter.eventSearchBox.trim());
    }

    filter.eventsType?.forEach((item: string) => {
      if (item) {
        params.append("event", item);
      }
    });
    filter.eventsMode?.forEach((item: string) => {
      if (item) {
        params.append("eventType", item);
      }
    });
    if (filter.school?.value) {
      params.set("venue", filter.school.value);
    }

    filter.school_id?.forEach((item: string) => {
      if (item) {
        params.append("schoolId", item);
      }
    });

    if (filter.currentPage && filter.currentPage > 1) {
      params.set("pageNo", String(filter.currentPage));
    }

    const newQuery = params.toString();
    const currentQuery = queryParams?.toString() || "";
    if (newQuery !== currentQuery) {
      router.replace(newQuery ? `/events?${newQuery}` : "/events", {
        scroll: false,
      });
    }
  }, [filter, queryParams, router]);
  // Measure actual header height so the sticky filter clears it at every resolution
  useEffect(() => {
    const measure = () => {
      const header = document.querySelector(".theme-main-menu");
      if (header) {
        setFilterTop(header.getBoundingClientRect().height + 8);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    const cartMap = new Map<string, any>();
    cart.forEach((val: any) => {
      cartMap.set(`${val.group_name}||${val.sub_type}`, val);
    });
    const updatedData: any = event.map((item: any) => {
      const match = cartMap.get(`${item.group_name}||${item["Sub Type"]}`);
      return {
        ...item,
        inCart: match ? match.inCart : 0,
      };
    });
    dispatch(setEvents(updatedData));
    const startIndex = (currentPage - 1) * itemsPerPage;
    dispatch(
      setPaginatedEvents(
        updatedData.slice(startIndex, startIndex + itemsPerPage),
      ),
    );
  }, [cart]);

  return (
    <div className="property-listing-six viva-listing-page md-80 pb-150 xl-pb-120">
      {loading && <Loader />}
      {toggleRemoveItemsModal && (
        <RemoveEventModal
          show={toggleRemoveItemsModal}
          closeEventModal={closeEventModal}
          removePreviousEvents={removePreviousEvents}
        />
      )}
      <div className="container container-large">
        <div className="row events-container">
          <div className="col-lg-8" id="cart-section">
            <div>
              <div className="viva-results-bar">
                <div>
                  Showing{" "}
                  <strong>
                    {paginatedEvents?.length > 0
                      ? (filter.currentPage - 1) * itemsPerPage + 1
                      : 0}
                  </strong>
                  {paginatedEvents?.length > 0 && (
                    <>
                      <span>–</span>
                      <strong>
                        {Math.min(filter.currentPage * itemsPerPage, count)}
                      </strong>
                    </>
                  )}{" "}
                  of <strong>{count}</strong> Results
                </div>
              </div>
              {paginatedEvents?.length > 0 ? (
                <div
                  className="viva-event-grid"
                  key={`${Date.now()}-page-${currentPage}`}
                >
                  {paginatedEvents?.map((item: any) => {
                    const pathname = `/event/${groupkebabCase(item?.participation_type)}/${groupkebabCase(item?.["Sub Type"])}/${groupkebabCase(item?.Category)}/${groupkebabCase(item?.group_name)}`;
                    // Carry the host school (venue) chosen in the filter forward
                    // to the detail page so its Venue is auto-selected.
                    const detailHref = params.toString()
                      ? `${pathname}?${params.toString()}`
                      : pathname;
                    const key = normalizeGroupKey(
                      [
                        item?.group_name,
                        item?.participation_type,
                        item?.Category,
                      ].join("|") || "",
                    );
                    const aggregate = aggregates[key] || null;

                    return (
                      <EventCard
                        key={item?.id ?? detailHref}
                        item={item}
                        pathname={detailHref}
                        aggregate={aggregate}
                        aggregateLoading={aggLoading}
                        userRole={userRoleVal}
                        cart={cart}
                        paginatedEvents={paginatedEvents}
                        onRegister={() => {
                          if (item.inCart > 0) {
                            // Already in cart — go straight to cart
                            router.push("/cart");
                          } else {
                            // Open the event detail page which has the registration wizard
                            router.push(detailHref);
                          }
                        }}
                        onBulkUpload={() => {
                          handleBulkUpload(item);
                        }}
                        onAddToCart={() => {
                          // Cart icon also opens the wizard for a guided experience
                          router.push(detailHref);
                        }}
                        onRemoveFromCart={async () => {
                          handleRemoveFromCart(item);
                        }}
                      />
                    );
                  })}
                </div>
              ) : (
                <div className="viva-empty-state">
                  <div className="viva-empty-state__icon">
                    <Image
                      src={add_to_cart_with_blue_background}
                      alt="No events"
                      style={{ width: "4rem", height: "4rem" }}
                    />
                  </div>
                  <div className="viva-empty-state__title">No events found</div>
                  <div>Try adjusting your filters to see more results.</div>
                </div>
              )}

              {paginatedEvents && paginatedEvents.length > 0 && (
                <div className="viva-pagination-row">
                  <div>
                    Showing {startItem} to {endItem} of {count}
                  </div>
                  <div>
                    <Pagination
                      count={Math.ceil(count / itemsPerPage)}
                      page={currentPage}
                      onChange={onPageChange}
                      shape="rounded"
                      color="primary"
                      sx={{
                        "& .MuiPaginationItem-root.Mui-selected": {
                          backgroundColor: "rgb(63, 65, 209)",
                          color: "#fff",
                        },
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="col-lg-4 order-lg-first sm-pt-30">
            {!isMobile ? (
              <div
                className="advance-search-panel"
                style={{
                  position: "sticky",
                  top: filterTop,
                  marginTop: "1rem",
                  maxHeight: `calc(100vh - ${filterTop + 16}px)`,
                  maxWidth: "500px",
                  overflowY: "auto",
                  scrollbarWidth: "none",
                }}
              >
                <div className="main-bg" ref={filterRef}>
                  <FilterForm
                    setcurrentPage={setcurrentPage}
                    handleResetFilter={handleResetFilter}
                    getData={getData}
                    currentPage={currentPage}
                    filter={filter}
                    addFilter={addFilter}
                    search={search}
                    setSearch={setSearch}
                    paginatedEvents={paginatedEvents}
                    schools={schools}
                    setSchools={setSchools}
                  />
                </div>
              </div>
            ) : (
              <>
                <button
                  className="viva-filter-toggle"
                  onClick={() => setShowFilter(true)}
                >
                  <Image
                    src={filterIcon}
                    alt="filterIcon"
                    className="lazy-img icon me-2"
                    style={{
                      height: "1.5rem",
                      width: "1.5rem",
                    }}
                  />
                  Filter
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      {showFilter && (
        <div
          className="modal-overlay position-fixed w-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-50"
          style={{
            zIndex: 1110,
            backdropFilter: "blur(3px)",
            top: "0",
            overflowY: "scroll",
            height: "100%",
          }}
        >
          <div
            className="advance-search-panel modal-content rounded text-center w-100"
            style={{
              position: "relative",
              // top: filterTop,
              margin: "1rem",
              maxWidth: "500px",
              maxHeight: `95vh`,
              overflowY: "auto",
              scrollbarWidth: "none",
            }}
          >
            <Image
              src={close}
              alt="close"
              className="position-absolute lazy-img icon me-2"
              style={{
                top: "7.75rem",
                right: "0.75rem",
                height: "2rem",
                width: "2rem",
              }}
              onClick={() => {
                setShowFilter(false);
              }}
            />
            <div
              className="main-bg"
              style={{
                margin: "auto 0.5rem",
              }}
            >
              <FilterForm
                setcurrentPage={setcurrentPage}
                handleResetFilter={handleResetFilter}
                getData={getData}
                currentPage={currentPage}
                filter={filter}
                addFilter={addFilter}
                search={search}
                setSearch={setSearch}
                paginatedEvents={paginatedEvents}
                schools={schools}
                setSchools={setSchools}
              />
            </div>
          </div>
        </div>
      )}
      {clearCart && <ClearCart />}
    </div>
  );
};

export default VivaEventListing;
